import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {DeviceId, ConsoleSessionId, ProjectId} from '../../types/ids';
import {AuthService} from '../../services/auth.service';
import {ConsoleSession, consoleSessionFromBackendObject} from '../../types/console-session';
import { GenericApiResponse} from '../apibase.api';
import { HttpClient, HttpParams } from '@angular/common/http';
import * as _ from 'lodash';
import {
    ConsoleSessionStatistics,
    consoleSessionStatisticsFromBackendObject, ConsoleStatistics, consoleStatisticsFromBackendObject,
} from '../../types/SessionStatistics';

@Injectable({
    providedIn: 'root'
})
export class ConsoleSessionApi {

    constructor(public OAuth2AuthService: AuthService, private http: HttpClient) {
    }

    requestConsoleOnDevice(deviceId: DeviceId, options: any = {}): Observable<ConsoleSession> {
        return this.http.post<any>(`/device/devices/${deviceId}/console`, options).pipe(map(response => {
            return consoleSessionFromBackendObject(response)
        }));
    }

    logConsoleActivity(consoleSessionId: ConsoleSessionId): Observable<number> {
        return this.http.post(`/console/${consoleSessionId}/log`, {}, {responseType: 'text'}).pipe(map(x => +x));
    }

    /** Signaling WebSocket path for viewing a headless console session's browser stream (mediasoup "viewer" protocol). */
    getHeadlessConsoleWebRtcSignalingURL(consoleSessionId: ConsoleSessionId): string {
        return `/console/${consoleSessionId}/headless/webrtc`;
    }

    getConsoleSessionById(consoleSessionId: ConsoleSessionId): Observable<ConsoleSession> {
        return this.http.get<any>('/console/sessions/' + consoleSessionId).pipe(map(response => consoleSessionFromBackendObject(response)));
    }

    terminateConsoleOnDevice(consoleSessionId: ConsoleSessionId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/console/' + consoleSessionId + '/terminate', {});
    }

    getConsoleSessionsForCurrentCustomer(count?: number, from?: string, until?: string): Observable<ConsoleSession[]> {
        let params = new HttpParams();
        if (from) {
            params = params.set('from', from);
        }
        if (until) {
            params = params.set('until', until);
        }
        if (count && _.isFinite(count)) {
            params = params.append('count', count.toString());
        }
        return this.http.get<Array<any>>('/console/sessions', {params: params}).pipe(map(responses => (responses ?? []).map(r => consoleSessionFromBackendObject(r))));
    }

    getConsoleSessionStatisticsForCurrentCustomer(count?: number, from?: string, until?: string, project?: ProjectId): Observable<ConsoleSessionStatistics[]> {
        let params = new HttpParams();
        if (from) params = params.set('from', from);
        if (until) params = params.set('until', until);
        if (project) params = params.set('project', project);
        if (count && _.isFinite(count)) params = params.set('count', count.toString());

        return this.http.get<Array<any>>('/console/sessionstatistics', {params: params})
            .pipe(map(response => (response ?? []).map(session => consoleSessionStatisticsFromBackendObject(session, project!))));
    }

    getConsoleUserStatisticsForCurrentCustomer(count?: number, from?: string, until?: string, project?: ProjectId): Observable<ConsoleSessionStatistics[]> {
        let params = new HttpParams();
        if (from) params = params.set('from', from);
        if (until) params = params.set('until', until);
        if (project) params = params.set('project', project);
        if (count && _.isFinite(count)) params = params.set('count', count.toString());

        return this.http.get<Array<any>>('/console/userstatistics', {params: params})
            .pipe(map(response => (response ?? []).map(session => consoleSessionStatisticsFromBackendObject(session, project!))));
    }

    getConsoleStatisticsForCurrentCustomer(from?: string, until?: string, project?: ProjectId): Observable<ConsoleStatistics> {
        let params = new HttpParams();
        if (from) params = params.set('from', from);
        if (until) params = params.set('until', until);
        if (project) params = params.set('project', project);

        return this.http.get<Array<any>>('/console/statistics', {params: params})
            .pipe(map(response => consoleStatisticsFromBackendObject(response, project!)));
    }

    getConsoleStatisticsForCurrentCustomerGlobalPerProject(from?: string, until?: string): Observable<ConsoleStatistics> {
        let params = new HttpParams();
        if (from) params = params.set('from', from);
        if (until) params = params.set('until', until);

        return this.http.get<any>('/console/statistics/globalPerProject', {params: params})
            .pipe(map(response => {
                let responseMap = new Map<string, ConsoleStatistics>(Object.entries(response));
                let platform: ConsoleSessionStatistics[] = [];
                let device: ConsoleSessionStatistics[] = [];
                let user: ConsoleSessionStatistics[] = [];
                responseMap.forEach((v, project) => {
                    let stats = consoleStatisticsFromBackendObject(v, project);
                    platform = platform.concat(stats.platform);
                    device = device.concat(stats.device);
                    user = user.concat(stats.user);
                });

                return {platform: platform, device: device, user: user} as ConsoleStatistics;
            }));
    }

}
